import moment from "moment";
import { formatTimeRange } from "./openHouseHelpers";
import { interpolate } from "./emailPlaceholders";

function fmtDate(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  return moment(yyyyMmDd).format("ddd, MMM D, YYYY");
}

export function firstNameOf(name) {
  if (!name) return "";
  const trimmed = String(name).trim();
  return trimmed ? trimmed.split(/\s+/)[0] : "";
}

// Common data bag for placeholder substitution across all builders.
export function dataFor(oh, fb) {
  return {
    propertyAddress: oh?.propertyAddress || "",
    date: oh?.date ? fmtDate(oh.date) : "",
    time: (oh?.startTime && oh?.endTime) ? formatTimeRange(oh.startTime, oh.endTime) : "",
    sellerName: oh?.sellerName || "",
    sellerFirstName: firstNameOf(oh?.sellerName),
    hostingAgentName: oh?.hostingAgentName || "",
    hostingAgentFirstName: firstNameOf(oh?.hostingAgentName),
    lockboxType: oh?.lockboxType || "",
    lockboxNumber: oh?.lockboxNumber || "",
    lockboxCode: oh?.lockboxCode || "",
    // recap-only:
    groups: fb?.groups ?? "",
    secondShowings: fb?.secondShowings ?? "",
    agentFeedback: fb?.agentFeedback || "",
    buyerFeedback: fb?.buyerFeedback || "",
    otherComments: fb?.otherComments || "",
  };
}

// Value for the Lockbox section in the hosting-agent email, or "" when no
// lockbox is assigned.
//   Push Button: include the access code, since the agent needs it to enter.
//   Electronic:  type only — the agent gets in via the manufacturer app (Supra
//                etc.) using their own credentials; the lockbox serial number
//                isn't actionable to share over email.
export function lockboxSectionValue(oh) {
  const type = oh?.lockboxType || "";
  if (!type) return "";
  if (type === "Push Button" && oh?.lockboxCode) {
    return `${type} (code ${oh.lockboxCode})`;
  }
  return type;
}

export const DEFAULT_TEMPLATES = {
  "scheduling-seller": {
    subject: "Open house scheduled — {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

Just a heads-up — we have an open house scheduled at your home on {{date}}, from {{time}}.

{{hostingAgentFirstName}} will be hosting. We'll follow up with a recap of the showing afterward.

If you have any questions or need to make changes, just reply to this email.

Thanks,
Team Edelstein`,
  },
  "scheduling-agent": {
    subject: "Open house — {{propertyAddress}} on {{date}}",
    body:
`Hi {{hostingAgentFirstName}},

You're scheduled to host an open house at {{propertyAddress}} on {{date}}, {{time}}.

After the open house, please share feedback at the link below.`,
  },
  "update-seller": {
    subject: "Open house update — {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

The open house at your home has been rescheduled.

{{hostingAgentFirstName}} will be hosting.`,
  },
  "update-agent": {
    subject: "Open house rescheduled — {{propertyAddress}}",
    body:
`Hi {{hostingAgentFirstName}},

Just a heads-up: the open house at {{propertyAddress}} has been moved.

Same public feedback link as before.`,
  },
  "cancellation-seller": {
    subject: "Open house cancelled — {{propertyAddress}}",
    body:
`Hi {{sellerFirstName}},

The open house at your home, scheduled for {{date}}, {{time}}, has been cancelled.

If you'd like to reschedule, just reply to this email.`,
  },
  "cancellation-agent": {
    subject: "Open house cancelled — {{propertyAddress}}",
    body:
`Hi {{hostingAgentFirstName}},

The open house at {{propertyAddress}} on {{date}}, {{time}}, has been cancelled.`,
  },
  "recap-seller": {
    subject: "Open house recap — {{propertyAddress}}",
    intro:
`Hi {{sellerFirstName}},

Here's the recap from the open house at your home on {{date}}, {{time}}.`,
    closing:
`Let me know if you'd like to talk through any of this.

Thanks,
Team Edelstein`,
  },

  // ===== Client Touchpoints (manual send) =====
  "birthday": {
    subject: "Happy birthday, {{contactFirstName}}!",
    body:
`Hi {{contactFirstName}},

Wishing you a very happy birthday today! Hope you have a wonderful day with the people you love.

Thinking of you,
{{senderFirstName}}
Team Edelstein`,
  },
  "home-anniversary": {
    subject: "Happy home anniversary, {{contactFirstName}}!",
    body:
`Hi {{contactFirstName}},

It's hard to believe — it's been {{yearsInHome}} year(s) since you made {{propertyAddress}} your home!

If you ever want to chat about the market, refinancing, or what your home might be worth today, I'm always here.

Cheers to many more,
{{senderFirstName}}
Team Edelstein`,
  },
  "closing-day": {
    subject: "Closing day — {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Today's the day! Congrats on closing on {{propertyAddress}}.

If anything comes up on the day of, call me directly. Otherwise, enjoy every moment of it.

Talk soon,
{{senderFirstName}}
Team Edelstein`,
  },
  "welcome": {
    subject: "Great to meet you, {{contactFirstName}}",
    body:
`Hi {{contactFirstName}},

Really enjoyed connecting. I'll be your point of contact going forward — anything real-estate related, just reach out.

A few things I'll send your way over the coming weeks: market snapshots for your area, listings worth a look, and a few resources you might find handy. If anything ever isn't useful, just let me know.

Talk soon,
{{senderFirstName}}
Team Edelstein`,
  },

  // ===== Marketing (manual send) =====
  "just-listed": {
    subject: "Just listed — {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Wanted you to be the first to know — we just brought a new listing to market at {{propertyAddress}}.

If you (or anyone you know) might be a fit, hit reply and I'll send over the full details.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "just-sold": {
    subject: "Just sold — {{propertyAddress}}",
    body:
`Hi {{contactFirstName}},

Quick update — we just closed on {{propertyAddress}}. Always a great feeling to hand over the keys.

If you're curious what's happening in your own neighborhood, I'd be happy to put together a quick snapshot. Just reply.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },

  // ===== Vendor Notifications (staging) =====
  "staging-consult-vendor": {
    subject: "Staging consult requested — {{propertyAddress}}",
    body:
`Hi {{vendorName}},

We'd like to schedule a staging consultation for our upcoming listing at {{propertyAddress}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockboxType}} #{{lockboxNumber}} (code {{lockboxCode}})

Please let me know what dates work on your end and I'll coordinate access.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "staging-install-vendor": {
    subject: "Staging install confirmed — {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Confirming the staging install at {{propertyAddress}} on {{installDate}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockboxType}} #{{lockboxNumber}} (code {{lockboxCode}})

The home will be ready and clear for your team. Reply if anything has changed.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "staging-destage-vendor": {
    subject: "Destage scheduled — {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Wanted to confirm the destage for {{propertyAddress}} on {{destageDate}}.

Closing date: {{closingDate}}
Lockbox: {{lockboxType}} #{{lockboxNumber}} (code {{lockboxCode}})

Thanks again for the great work.

{{senderFirstName}}
Team Edelstein`,
  },
  "photographer-vendor": {
    subject: "Photoshoot confirmed — {{propertyAddress}}",
    body:
`Hi {{vendorName}},

Confirming the photoshoot at {{propertyAddress}} on {{photoshootDate}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockboxType}} #{{lockboxNumber}} (code {{lockboxCode}})

The home will be staged and ready. Reply if anything has changed.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
  "pre-inspection-vendor": {
    subject: "Pre-listing inspection requested — {{propertyAddress}}",
    body:
`Hi {{vendorName}},

We'd like to schedule a pre-listing inspection at {{propertyAddress}}.

Sellers: {{clients}}
Target list date: {{targetListDate}}
Lockbox: {{lockboxType}} #{{lockboxNumber}} (code {{lockboxCode}})

Inspections requested:
{{inspectionList}}

Please let me know what dates work on your end and I'll coordinate access.

Thanks,
{{senderFirstName}}
Team Edelstein`,
  },
};

export function feedbackUrlFor(oh, origin) {
  const base =
    origin ||
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    "";
  const params = new URLSearchParams({
    id: oh.id,
    t: oh.feedbackToken || "",
  });
  if (oh.propertyAddress) params.set("a", oh.propertyAddress);
  if (oh.date) params.set("d", oh.date);
  if (oh.startTime) params.set("s", oh.startTime);
  if (oh.endTime) params.set("e", oh.endTime);
  // HashRouter: route lives behind `#`, otherwise login redirect eats the path
  return `${base}/#/feedback?${params.toString()}`;
}

export function buildSchedulingSellerEmail(oh, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["scheduling-seller"];
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    interpolate(overrides.body    ?? d.body,    data),
  };
}

export function buildSchedulingAgentEmail(oh, origin, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["scheduling-agent"];
  const url = feedbackUrlFor(oh, origin);
  const userBody = interpolate(overrides.body ?? d.body, data);
  const sellerBlock = oh?.sellerName ? `\n\nSeller: ${oh.sellerName}` : "";
  const lockboxValue = lockboxSectionValue(oh);
  const lockboxBlock = lockboxValue ? `\n\nLockbox: ${lockboxValue}` : "";
  const notesBlock = (oh?.notes && oh.notes.trim()) ? `\n\nNotes: ${oh.notes.trim()}` : "";
  const linkBlock = `\n\n${url}`;
  const signOff = "\n\nThanks for covering!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    userBody + sellerBlock + lockboxBlock + notesBlock + linkBlock + signOff,
  };
}

export function buildUpdateSellerEmail(oh, oldFields, newFields, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["update-seller"];
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const userBody = interpolate(overrides.body ?? d.body, data);
  const timeBlock = `\n\nPreviously: ${oldStr}\nNow: ${newStr}`;
  const signOff = "\n\nThanks,\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    userBody + timeBlock + signOff,
  };
}

export function buildUpdateAgentEmail(oh, oldFields, newFields, origin, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["update-agent"];
  const oldStr = `${fmtDate(oldFields.date)}, ${formatTimeRange(oldFields.startTime, oldFields.endTime)}`;
  const newStr = `${fmtDate(newFields.date)}, ${formatTimeRange(newFields.startTime, newFields.endTime)}`;
  const url = feedbackUrlFor(oh, origin);
  const userBody = interpolate(overrides.body ?? d.body, data);
  const timeBlock = `\n\nPreviously: ${oldStr}\nNow: ${newStr}`;
  const linkBlock = `\n\n${url}`;
  const signOff = "\n\nThanks!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    userBody + timeBlock + linkBlock + signOff,
  };
}

export function buildCancellationSellerEmail(oh, reason, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["cancellation-seller"];
  const userBody = interpolate(overrides.body ?? d.body, data);
  const reasonBlock = (reason && reason.trim()) ? `\n\nReason: ${reason.trim()}` : "";
  const signOff = "\n\nThanks,\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    userBody + reasonBlock + signOff,
  };
}

export function buildCancellationAgentEmail(oh, reason, overrides = {}) {
  const data = dataFor(oh);
  const d = DEFAULT_TEMPLATES["cancellation-agent"];
  const userBody = interpolate(overrides.body ?? d.body, data);
  const reasonBlock = (reason && reason.trim()) ? `\n\nReason: ${reason.trim()}` : "";
  const signOff = "\n\nThanks!\nTeam Edelstein";
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body:    userBody + reasonBlock + signOff,
  };
}

export function buildRecapSellerEmail(oh, fb, overrides = {}) {
  const data = dataFor(oh, fb);
  const d = DEFAULT_TEMPLATES["recap-seller"];
  // Apply smart formatting to feedback date/time (existing behavior preserved).
  const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(String(fb.date || "").trim())
    ? fmtDate(fb.date)
    : (fb.date || fmtDate(oh.date));
  const timeMatch = String(fb.time || "").trim().match(/^(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})$/);
  const timeStr = timeMatch
    ? formatTimeRange(`${timeMatch[1]}:${timeMatch[2]}`, `${timeMatch[3]}:${timeMatch[4]}`)
    : (fb.time || formatTimeRange(oh.startTime, oh.endTime));
  data.date = dateStr;
  data.time = timeStr;

  const intro = interpolate(overrides.intro ?? d.intro, data);
  const closing = interpolate(overrides.closing ?? d.closing, data);

  const middle = [
    "",
    `Hosted by: ${fb.agentName}`,
    `Groups attended: ${fb.groups}`,
    `Potential second showings: ${fb.secondShowings}`,
    "",
    "What showing agents said:",
    fb.agentFeedback,
    "",
    "What potential buyers said:",
    fb.buyerFeedback,
  ];
  if (fb.otherComments && fb.otherComments.trim()) {
    middle.push("", "Other comments:", fb.otherComments.trim());
  }
  const body = intro + middle.join("\n") + "\n\n" + closing;
  return {
    subject: interpolate(overrides.subject ?? d.subject, data),
    body,
  };
}

export function buildInviteResendEmail(oh, origin, overrides = {}) {
  return buildSchedulingAgentEmail(oh, origin, overrides);
}
